// Amadeus normalize + validate. The normalized shape matches the common
// FlightOffer typedef in providers/types.js. Amadeus is already close to
// the canonical shape; the adapter adds `_provider` + frozen `_raw` +
// convenience fields (firstDeparture, lastArrival, totalDuration, …) while
// preserving price decimals as strings and keeping the raw-style nested
// includedCheckedBags / additionalServices / amenities shapes so legacy
// fare / bag / markup code keeps working without a rewrite.
//
// Every function that needs provider state (display_carrier, display_airport,
// capability lookups) takes the `provider` instance as its first argument.
// Pure helpers come from `../base/helpers`.

import {
  as_string,
  as_number,
  bag_description,
  as_boolean,
  is_non_empty_array,
  build_empty_endpoint,
  build_meta,
  build_frozen_raw,
  sum_itinerary_durations,
  collect_marketing_carriers,
  with_open_jaw_endpoints,
  with_currency_symbol,
  make_price,
  finalize_itinerary,
  lift_first_fare_amenities,
  has_homogeneous_baggage,
} from '../base/helpers';
import { is_infant } from '@liveflight/utilities/travelers';

// Amadeus' includedCheckedBags may carry { quantity }, { weight, weightUnit }
// or both. "Weight present without explicit quantity" means 1 piece.
// Quantity ships as either a number or a numeric string ("2") in legacy
// responses; coerce rather than strict-typecheck.
const resolve_included_bag_quantity = (included) => {
  const safe = included || {};
  const quantity_n = Number(safe.quantity);
  if (Number.isFinite(quantity_n) && quantity_n > 0) {
    return quantity_n;
  }
  const weight_n = Number(safe.weight);
  if (Number.isFinite(weight_n) && weight_n > 0) {
    return 1;
  }
  return 0;
};

// ─── Shape builders ──────────────────────────────────────────────────────

const build_endpoint = (provider, raw_endpoint) => {
  const safe = raw_endpoint || {};
  const iata = as_string(safe.iataCode);
  return {
    iataCode: iata,
    terminal: as_string(safe.terminal),
    at: as_string(safe.at),
    airportName: provider.display_airport(iata),
    cityCode: '',
    cityName: provider.display_location(iata),
  };
};

const build_carrier = (provider, code) => {
  const safe_code = as_string(code);
  return {
    code: safe_code,
    name: provider.display_carrier(safe_code),
    alliance: '',
  };
};

const pick_fare_reference_pricing = (traveler_pricings) => {
  const list = Array.isArray(traveler_pricings) ? traveler_pricings : [];
  return list.find((p) => p && !is_infant(p)) || list[0] || null;
};

const find_fare_detail_for_segment = (traveler_pricings, segment_id) => {
  const reference = pick_fare_reference_pricing(traveler_pricings);
  if (!reference || !Array.isArray(reference.fareDetailsBySegment)) {
    return null;
  }
  return (
    reference.fareDetailsBySegment.find(
      (fd) => fd && fd.segmentId === segment_id
    ) || null
  );
};

const build_segment = (provider, raw_segment, traveler_pricings) => {
  const safe = raw_segment || {};
  const operating = safe.operating || {};
  const aircraft = safe.aircraft || {};
  const fare_detail = find_fare_detail_for_segment(traveler_pricings, safe.id);
  return {
    id: as_string(safe.id),
    departure: build_endpoint(provider, safe.departure),
    arrival: build_endpoint(provider, safe.arrival),
    carrier: build_carrier(provider, safe.carrierCode),
    operatingCarrier: build_carrier(
      provider,
      operating.carrierCode || safe.carrierCode
    ),
    flightNumber: as_string(safe.number),
    aircraft: {
      code: as_string(aircraft.code),
      name: '',
    },
    duration: as_string(safe.duration),
    numberOfStops: as_number(safe.numberOfStops),
    blacklistedInEU: as_boolean(safe.blacklistedInEU),
    cabin: fare_detail ? as_string(fare_detail.cabin) : '',
    fareClass: fare_detail ? as_string(fare_detail.class) : '',
    fareBasis: fare_detail ? as_string(fare_detail.fareBasis) : '',
  };
};

const build_itinerary = (provider, raw_itinerary, index, traveler_pricings) => {
  const safe = raw_itinerary || {};
  const segments = (Array.isArray(safe.segments) ? safe.segments : []).map(
    (seg) => build_segment(provider, seg, traveler_pricings)
  );
  return finalize_itinerary({
    id: `itin-${index}`,
    duration: as_string(safe.duration),
    segments,
    empty_endpoint: build_empty_endpoint(),
  });
};

const build_included_bags = (raw_bags) => {
  const safe = raw_bags || {};
  const weight_unit = as_string(
    safe.weightUnit ? safe.weightUnit.toLowerCase() : ''
  );
  return {
    quantity: resolve_included_bag_quantity(safe),
    weight: as_number(safe.weight),
    weightUnit: weight_unit,
  };
};

// Convert Amadeus's raw additionalServices Object
// {chargeableSeatNumber?, chargeableCheckedBags?} into the canonical
// FareDetailAdditionalService[] array shape from providers/types.js.
// The original raw values are kept on each entry's `_raw` so any
// provider-specific mutation path can read them back.
const build_fare_detail_additional_services = (raw_object) => {
  const { chargeableSeatNumber = '', chargeableCheckedBags = null } =
    raw_object || {};
  const out = [];
  if (chargeableSeatNumber) {
    out.push({
      type: 'seat',
      seatNumber: as_string(chargeableSeatNumber),
      _raw: { chargeableSeatNumber },
    });
  }
  if (chargeableCheckedBags) {
    const {
      id = '',
      quantity = undefined,
      weight = undefined,
      weightUnit = '',
    } = chargeableCheckedBags;
    const entry = { type: 'bag', _raw: chargeableCheckedBags };
    if (id) entry.id = as_string(id);
    if (quantity !== undefined) entry.quantity = as_number(quantity);
    if (weight !== undefined) entry.weight = as_number(weight);
    if (weightUnit) entry.weightUnit = as_string(weightUnit);
    entry.name = bag_description(chargeableCheckedBags);
    out.push(entry);
  }
  return out;
};

// Amadeus does not surface a per-fare hand-bag allowance, so we fall back
// to a flat heuristic: every pax, infants included, gets one personal item.
const build_fare_detail = (raw_detail) => {
  const safe = raw_detail || {};
  return {
    segmentId: as_string(safe.segmentId),
    cabin: as_string(safe.cabin),
    fareBasis: as_string(safe.fareBasis),
    fareClass: as_string(safe.class),
    brandedFare: as_string(safe.brandedFare),
    brandedFareLabel: as_string(safe.brandedFareLabel),
    includedCheckedBags: build_included_bags(safe.includedCheckedBags),
    includedCabinBags: build_included_bags(safe.includedCabinBags),
    includedHandBags: { quantity: 1 },
    additionalServices: build_fare_detail_additional_services(
      safe.additionalServices
    ),
    amenities: Array.isArray(safe.amenities) ? safe.amenities : [],
  };
};

const build_traveler_pricing = (raw_pricing) => {
  const safe = raw_pricing || {};
  const price = safe.price || {};
  const taxes = Array.isArray(price.taxes)
    ? price.taxes.map((t) => ({
        code: as_string(t.code),
        amount: as_string(t.amount),
      }))
    : [];
  const fare_details = Array.isArray(safe.fareDetailsBySegment)
    ? safe.fareDetailsBySegment.map((d) => build_fare_detail(d))
    : [];
  return {
    travelerId: as_string(safe.travelerId),
    travelerType: safe.travelerType,
    associatedAdultId: as_string(safe.associatedAdultId),
    fareOption: as_string(safe.fareOption, 'STANDARD'),
    price: with_currency_symbol({
      currency: as_string(price.currency),
      total: as_string(price.total),
      base: as_string(price.base),
      taxes,
    }),
    fareDetailsBySegment: fare_details,
  };
};

// Each Amadeus additionalServices entry already carries `{type, amount}`
// (Amadeus convention). Map `amount` into a canonical Price block per
// the AdditionalService typedef in providers/types.js.
const build_offer_additional_service = (raw_entry, currency_code) => {
  const { type = '', amount = '0' } = raw_entry || {};
  return {
    type: as_string(type),
    price: make_price(as_string(amount), currency_code),
  };
};

const build_price = (raw_price) => {
  const safe = raw_price || {};
  const fees = Array.isArray(safe.fees)
    ? safe.fees.map((f) => ({
        type: as_string(f.type),
        amount: as_string(f.amount),
      }))
    : [];
  const currency_code = as_string(safe.currency);
  const additional_services = Array.isArray(safe.additionalServices)
    ? safe.additionalServices.map((entry) =>
        build_offer_additional_service(entry, currency_code)
      )
    : [];
  return with_currency_symbol({
    currency: currency_code,
    base: as_string(safe.base),
    _markup_base: as_string(safe.total),
    total: as_string(safe.total),
    grandTotal: as_string(safe.grandTotal, as_string(safe.total)),
    fees,
    additionalServices: additional_services,
  });
};

const infer_fare_info = (itineraries, traveler_pricings) => {
  const first_segment = itineraries[0] && itineraries[0].segments[0];
  const cabin = first_segment ? first_segment.cabin : '';
  const reference = pick_fare_reference_pricing(traveler_pricings);
  const first_detail =
    reference &&
    reference.fareDetailsBySegment &&
    reference.fareDetailsBySegment[0]
      ? reference.fareDetailsBySegment[0]
      : null;
  return {
    brand: first_detail ? as_string(first_detail.fareBasis) : '',
    cabin,
    refundable: false,
    changeable: false,
    rulesUri: '',
  };
};

// Normalize a single Amadeus amenity item to a flat shape. The amenity set
// returned upstream is left as-is — no predetermined boolean keys, no
// inference. An offer without amenities yields [].
const build_amenity = (raw_amenity) => {
  const safe = raw_amenity || {};
  const provider_info = safe.amenityProvider || {};
  return {
    code: as_string(safe.code),
    description: as_string(safe.description),
    isChargeable: as_boolean(safe.isChargeable),
    amenityType: as_string(safe.amenityType),
    amenityProvider: as_string(provider_info.name),
  };
};

const collect_amenities = (traveler_pricings) =>
  lift_first_fare_amenities(traveler_pricings, build_amenity);

// ─── Contract ────────────────────────────────────────────────────────────

/**
 * @param {Provider} _provider
 * @param {Object} raw raw Amadeus offer
 * @returns {{ valid: boolean, reason?: string }}
 */
export const validate = (_provider, raw, options = {}) => {
  const { lenient_price = false } = options;
  if (!raw || typeof raw !== 'object')
    {return { valid: false, reason: 'raw is not an object' };}
  if (!is_non_empty_array(raw.itineraries))
    {return { valid: false, reason: 'itineraries missing or empty' };}
  const itineraries_ok = raw.itineraries.every(
    (itin) => itin && is_non_empty_array(itin.segments)
  );
  if (!itineraries_ok)
    {return { valid: false, reason: 'an itinerary has no segments' };}
  if (!is_non_empty_array(raw.travelerPricings))
    {return { valid: false, reason: 'travelerPricings missing or empty' };}
  // Lenient mode accepts an offer whose pricing is absent or incomplete.
  if (lenient_price) return { valid: true };
  if (!raw.price) return { valid: false, reason: 'price missing' };
  // Amadeus historically ships price.total as a string but some legacy
  // responses use a number — coerce-check rather than strict typeof.
  if (raw.price.total == null || Number.isNaN(Number(raw.price.total))) {
    return { valid: false, reason: 'price.total not numeric' };
  }
  if (
    typeof raw.price.currency !== 'string' ||
    raw.price.currency.length === 0
  ) {
    return { valid: false, reason: 'price.currency not a non-empty string' };
  }
  return { valid: true };
};

/**
 * Convert a raw Amadeus offer to the canonical FlightOffer shape.
 *
 * @param {Provider} provider
 * @param {Object} raw raw Amadeus offer
 * @returns {Object} normalized FlightOffer (see providers/types.js)
 * @throws {Error} when `raw` doesn't pass `validate`
 */
export const normalize = (provider, raw, options = {}) => {
  const { valid, reason } = validate(provider, raw, options);
  if (!valid) {
    throw new Error(
      `[AmadeusProvider] payload does not conform to Amadeus shape: ${reason}`
    );
  }

  const traveler_pricings_raw = raw.travelerPricings;
  const itineraries = raw.itineraries.map((itin, idx) =>
    build_itinerary(provider, itin, idx, traveler_pricings_raw)
  );
  const traveler_pricings = traveler_pricings_raw.map((tp) =>
    build_traveler_pricing(tp)
  );
  const price = build_price(raw.price);
  const fare = infer_fare_info(itineraries, traveler_pricings);
  const amenities = collect_amenities(traveler_pricings);
  const validating_codes = Array.isArray(raw.validatingAirlineCodes)
    ? raw.validatingAirlineCodes.slice()
    : [];

  const flagged_itineraries = with_open_jaw_endpoints(itineraries);
  const first_itin = flagged_itineraries[0];
  const last_itin = flagged_itineraries[flagged_itineraries.length - 1];

  const offer_id = as_string(raw.id);

  return {
    _provider: provider.providerId,
    _raw: build_frozen_raw(raw),
    _meta: build_meta(provider.version),
    _key: `${provider.providerId}:${offer_id}`,

    id: offer_id,
    providerOfferId: offer_id,
    providerSessionId: '',
    source: as_string(raw.source, 'GDS'),

    firstDeparture: first_itin
      ? first_itin.firstDeparture
      : build_empty_endpoint(),
    firstArrival: first_itin ? first_itin.lastArrival : build_empty_endpoint(),
    lastDeparture: last_itin
      ? last_itin.firstDeparture
      : build_empty_endpoint(),
    lastArrival: last_itin ? last_itin.lastArrival : build_empty_endpoint(),
    totalDuration: sum_itinerary_durations(itineraries),
    oneWay: as_boolean(raw.oneWay),
    numberOfBookableSeats: as_number(raw.numberOfBookableSeats),
    lastTicketingDate: as_string(raw.lastTicketingDate),

    validatingAirlineCodes: validating_codes,
    marketingCarrierCodes: collect_marketing_carriers(itineraries),

    itineraries: flagged_itineraries,
    travelerPricings: traveler_pricings,
    bagsHomogeneous: has_homogeneous_baggage({
      travelerPricings: traveler_pricings,
    }),

    price,
    fare,
    refund: {
      allowed: false,
      penaltyCurrency: price.currency,
      penaltyAmount: '0',
      notes: '',
    },
    change: {
      allowed: true,
      penaltyCurrency: price.currency,
      penaltyAmount: '0',
      notes: '',
    },
    payment: { acceptedMethods: [], requires3DS: false, paymentContext: '' },
    amenities,
  };
};

/**
 * Extract the offers array from any of the three shapes the search API
 * surfaces through axios:
 *   - axios envelope:   { data: { data: [...], dictionaries }, status, ... }
 *   - Amadeus body:     { data: [...], dictionaries }
 *   - bare offer array: [ ... ]
 *
 * @param {Object|Array} response
 * @returns {Array}
 */
export const extract_offers_array = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  if (response.data && Array.isArray(response.data.data)) {
    return response.data.data;
  }
  return [];
};
